import { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, CircularProgress, TextField, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { getCurrentMonthRange, isValidDateRange, getCurrentDate } from '../../../utils/dateUtils';
import Pagination from '../../shared/Pagination';
import '../Subcontract/Index.css';


const StampingUpdateIndex = () => {
    const { token, companyId, isAuthenticated, logout, userId } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [updateDates, setUpdateDates] = useState({});
    const [updating, setUpdating] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Initialize with current month range
    const defaultRange = getCurrentMonthRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [dateError, setDateError] = useState('');

    // Validate and correct dates on component mount
    useEffect(() => {
        const currentFromDate = fromDate;
        const currentToDate = toDate;
        
        if (!isValidDateRange(currentFromDate, currentToDate)) {
            console.log('Invalid date range detected, correcting...', { currentFromDate, currentToDate });
            const correctedRange = getCurrentMonthRange();
            setFromDate(correctedRange.fromDate);
            setToDate(correctedRange.toDate);
            setDateError('');
        }
    }, []);

    // Validate dates whenever they change
    useEffect(() => {
        if (fromDate && toDate && !isValidDateRange(fromDate, toDate)) {
            setDateError('Invalid date range: From date cannot be after To date');
        } else if (dateError && fromDate && toDate && isValidDateRange(fromDate, toDate)) {
            setDateError('');
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        // Only load data if we have valid dates
        if (isValidDateRange(fromDate, toDate)) {
            fetchStampingUpdates();
        }
    }, [isAuthenticated, token, fromDate, toDate]);

    // Date validation functions
    const handleFromDateChange = (newFromDate) => {
        setDateError('');
        
        if (!isValidDateRange(newFromDate, toDate)) {
            setDateError('From date cannot be after To date');
            return;
        }
        
        if (new Date(newFromDate) > new Date(getCurrentDate())) {
            setDateError('From date cannot be in the future');
            return;
        }
        
        setFromDate(newFromDate);
    };

    const handleToDateChange = (newToDate) => {
        setDateError('');
        
        if (!isValidDateRange(fromDate, newToDate)) {
            setDateError('To date cannot be before From date');
            return;
        }
        
        if (new Date(newToDate) > new Date(getCurrentDate())) {
            setDateError('To date cannot be in the future');
            return;
        }
        
        setToDate(newToDate);
    };

    // Function to fix invalid date ranges
    const fixDateRange = () => {
        const correctedRange = getCurrentMonthRange();
        setFromDate(correctedRange.fromDate);
        setToDate(correctedRange.toDate);
        setDateError('');
    };

    const fetchStampingUpdates = async () => {
        try {
            setLoading(true);
            const url = `/StampingUpdates/${companyId}?fromDate=${fromDate}&toDate=${toDate}`;
            console.log('Fetching Stamping Updates with URL:', url);
            const response = await fetchData('ReminderApi', url, token);
            console.log('Stamping Updates Response:', response);
            if (response && Array.isArray(response)) {
                setData(response);
            } else {
                setData([]);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching stamping updates:', error);
            setData([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        const filtered = data.filter((item) =>
            (item.PoNumber && item.PoNumber.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
            (item.poNumber && item.poNumber.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
            (item.Customer && item.Customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (item.customer && item.customer.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (item.CustomerName && item.CustomerName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        setFilteredData(filtered);
    }, [data, searchTerm]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allIds = filteredData.map(item => item.Id || item.id);
            setSelectedItems(allIds);
            
            // Auto-fill current date for all items
            const currentDate = new Date().toISOString().split('T')[0];
            const newDates = {};
            filteredData.forEach((item, index) => {
                const dateKey = `${item.Id || item.id}-${index}`;
                newDates[dateKey] = currentDate;
            });
            setUpdateDates(newDates);
        } else {
            setSelectedItems([]);
            setUpdateDates({});
        }
    };

    const handleSelectItem = (id, index) => {
        setSelectedItems(prev => {
            const isCurrentlySelected = prev.includes(id);
            
            if (isCurrentlySelected) {
                // Remove from selection and clear date
                setUpdateDates(prevDates => {
                    const dateKey = `${id}-${index}`;
                    const newDates = { ...prevDates };
                    delete newDates[dateKey];
                    return newDates;
                });
                return prev.filter(itemId => itemId !== id);
            } else {
                // Add to selection and auto-fill current date
                const currentDate = new Date().toISOString().split('T')[0];
                const dateKey = `${id}-${index}`;
                setUpdateDates(prevDates => ({
                    ...prevDates,
                    [dateKey]: currentDate
                }));
                return [...prev, id];
            }
        });
    };

    const prepareStampingPayload = (item, modalUpdateDate, currentUserId) => {
        // Priority: Use the date picked from calendar (modalUpdateDate) if available
        const candidate = modalUpdateDate || item.StampingUpdateDate || item.stampingUpdateDate || new Date().toISOString().split('T')[0];
        console.log('Stamping Payload - Item:', item, 'Selected Date:', modalUpdateDate, 'Final Date:', candidate);
        
        // Match the ServiceUpdate model structure exactly - include all required fields
        // Note: For stamping, we use ServiceUpdateDate field (not StampingUpdateDate) since the model only has ServiceUpdateDate
        return {
            Id: item.Id ?? item.id ?? 0,
            CompanyId: item.CompanyId ?? item.companyId ?? companyId,
            PurchaseId: item.PurchaseOrderId ?? item.PurchaseId ?? item.purchaseId ?? 0,
            CustomerId: item.CustomerId ?? item.customerId ?? 0,
            StampingUpdateDate: candidate,  // Using ServiceUpdateDate for stamping too
            IsStamping: true,  // False for stamping
            IsActive: item.IsActive ?? true,
            CreatedDate: item.CreatedDate ?? new Date().toISOString(),
            CreatedBy: item.CreatedBy ?? currentUserId,
            UpdatedDate: new Date().toISOString(),
            UpdatedBy: currentUserId,
            BillId: item.BillId ?? 0,
            BillNo: item.BillNo ?? "",  // Required field
            IsBilled: item.IsBilled ?? false,
            ReceiptId: item.ReceiptId ?? 0,
            ReceiptNo: item.ReceiptNo ?? "",  // Required field
            IsReceipt: item.IsReceipt ?? false,
            QuarterNo: item.QuarterNo ?? 0,
            Customer: null,
        };
    };

    const updateStampingApi = async (items) => {
        console.log('Calling Stamping Update API with items:', items);
        const response = await postData("StampApi/UpdateList", items, token);
        console.log('Stamping Update API Response:', response);
        return { success: response?.success === true };
    };

    const handleUpdateSelected = async () => {
        if (selectedItems.length === 0) {
            alert('Please select at least one item to update');
            return;
        }

        // Validate that all selected items have dates
        const missingDates = [];
        selectedItems.forEach(id => {
            const item = filteredData.find(i => (i.Id || i.id) === id);
            const index = filteredData.indexOf(item);
            const dateKey = `${id}-${index}`;
            
            if (!updateDates[dateKey]) {
                missingDates.push(item);
            }
        });

        if (missingDates.length > 0) {
            alert(`Please select dates for all selected items. ${missingDates.length} item(s) missing dates.`);
            return;
        }

        try {
            setUpdating(true);
            
            // Prepare payload for all selected items
            const payloadItems = [];
            
            selectedItems.forEach(id => {
                const item = filteredData.find(i => (i.Id || i.id) === id);
                const index = filteredData.indexOf(item);
                const dateKey = `${id}-${index}`;
                const selectedDate = updateDates[dateKey];
                
                if (item) {
                    const payload = prepareStampingPayload(item, selectedDate, userId);
                    payloadItems.push(payload);
                }
            });

            console.log('Prepared payloads for bulk update:', payloadItems);

            // Call the API
            const result = await updateStampingApi(payloadItems);

            if (result.success) {
                alert(`Successfully updated ${selectedItems.length} stamping(s)`);
                
                // Clear selections and dates
                setSelectedItems([]);
                setUpdateDates({});
                
                // Refresh data
                await fetchStampingUpdates();
            } else {
                alert('Failed to update stampings. Please try again.');
            }
        } catch (error) {
            console.error('Error updating stampings:', error);
            alert('An error occurred while updating stampings. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const handleDateChange = (itemId, date, index) => {
        setUpdateDates(prev => ({
            ...prev,
            [`${itemId}-${index}`]: date
        }));
    };

    // Pagination logic
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, fromDate, toDate]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };


    return (
        <div className="subcontract-index-wrapper">
            {/* Header */}
            <div className="subcontract-index-header">
                <div className="header-left">
                    <h1 className="index-title">Stamping Updates</h1>
                   
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                {dateError && (
                    <div style={{ 
                        color: '#dc2626', 
                        fontSize: '14px', 
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span>{dateError}</span>
                        <Button
                            size="small"
                            onClick={fixDateRange}
                            style={{
                                marginLeft: '12px',
                                fontSize: '12px',
                                padding: '4px 8px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                textTransform: 'none'
                            }}
                        >
                            Fix Dates
                        </Button>
                    </div>
                )}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>From:</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => handleFromDateChange(e.target.value)}
                            max={getCurrentDate()}
                            style={{
                                padding: '8px 12px',
                                border: dateError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>To:</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            min={fromDate}
                            max={getCurrentDate()}
                            style={{
                                padding: '8px 12px',
                                border: dateError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                </div>
                <TextField
                    placeholder="Search by PO Number or Customer Name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                {selectedItems.length > 0 && (
                    <Button
                        variant="contained"
                        onClick={handleUpdateSelected}
                        disabled={updating}
                        style={{
                            background: updating ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '10px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            cursor: updating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {updating ? (
                            <>
                                <CircularProgress size={16} style={{ marginRight: '8px', color: 'white' }} />
                                Updating...
                            </>
                        ) : (
                            `Update Selected (${selectedItems.length})`
                        )}
                    </Button>
                )}
            </div>

            {/* Table */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-container">
                        <CircularProgress />
                        <p>Loading stamping updates...</p>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📋</div>
                        <h3>No Stamping Updates Found</h3>
                        <p>No stamping renewals are due at this time</p>
                    </div>
                ) : (
                    <TableContainer component={Paper} className="modern-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="table-header" padding="checkbox">
                                        <input
                                            type="checkbox"
                                            onChange={handleSelectAll}
                                            checked={filteredData.length > 0 && selectedItems.length === filteredData.length}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#667eea' }}
                                        />
                                    </TableCell>
                                    <TableCell className="table-header">Sl No</TableCell>
                                    <TableCell className="table-header">PO Number</TableCell>
                                    <TableCell className="table-header">Customer Name</TableCell>
                                   
                                    <TableCell className="table-header">Update Date</TableCell>
                                    <TableCell className="table-header">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((item, index) => {
                                    // Check if completed
                                    const isCompleted = item.isStamping === true || item.IsStamping === true;
                                    
                                    // Determine status
                                    let statusText = 'Upcoming';
                                    let statusClass = 'status-upcoming';
                                    
                                    if (isCompleted) {
                                        statusText = 'Completed';
                                        statusClass = 'status-completed';
                                    } else if (item.Status !== undefined) {
                                        if (item.Status === 3) {
                                            statusText = 'Completed';
                                            statusClass = 'status-completed';
                                        } else if (item.Status === 2) {
                                            statusText = 'Pending';
                                            statusClass = 'status-pending';
                                        } else if (item.Status === 1) {
                                            statusText = 'Upcoming';
                                            statusClass = 'status-upcoming';
                                        }
                                    } else if (item.StampingToDate) {
                                        const currentMonth = new Date().getMonth();
                                        const currentYear = new Date().getFullYear();
                                        const date = new Date(item.StampingToDate);
                                        if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
                                            statusText = 'Pending';
                                            statusClass = 'status-pending';
                                        }
                                    }
                                    
                                    return (
                                    <TableRow key={item.Id || item.id || index} className="table-row">
                                        {!isCompleted && (
                                            <TableCell className="table-cell" padding="checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedItems.includes(item.Id || item.id)}
                                                    onChange={() => handleSelectItem(item.Id || item.id, index)}
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#667eea' }}
                                                />
                                            </TableCell>
                                        )}
                                        {isCompleted && <TableCell className="table-cell"></TableCell>}
                                        
                                        <TableCell className="table-cell">
                                            <span className="serial-number">{startIndex + index + 1}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="subcontract-name">{item.PoNumber || item.poNumber || item.pOnumber || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="contact-text">{item.Customer || item.customer || item.CustomerName || item.customername || 'N/A'}</span>
                                        </TableCell>
                                       
                                        <TableCell className="table-cell">
                                            {isCompleted ? (
                                                <span style={{ 
                                                    fontSize: '13px', 
                                                    color: '#065f46',
                                                    fontWeight: 500,
                                                    padding: '8px 12px',
                                                    background: '#d1fae5',
                                                    borderRadius: '6px',
                                                    display: 'inline-block'
                                                }}>
                                                    {item.stampingUpdateDate || item.StampingUpdateDate 
                                                        ? new Date(item.stampingUpdateDate || item.StampingUpdateDate).toLocaleDateString('en-GB')
                                                        : 'N/A'}
                                                </span>
                                            ) : (
                                                <input
                                                    type="date"
                                                    value={updateDates[`${item.Id || item.id}-${index}`] || ''}
                                                    onChange={(e) => handleDateChange(item.Id || item.id, e.target.value, index)}
                                                    className="date-picker-input"
                                                    style={{
                                                        padding: '8px 12px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '13px',
                                                        width: '150px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className={`status-badge ${statusClass}`}>
                                                {statusText}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            {/* Pagination */}
            {!loading && filteredData.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            )}

            {/* Summary */}
            {!loading && filteredData.length > 0 && (
                <div className="summary-section">
                    <span className="summary-text">
                        Total: {data.length} stamping updates
                        {selectedItems.length > 0 && ` • ${selectedItems.length} selected`}
                    </span>
                </div>
            )}
        </div>
    );
};

export default StampingUpdateIndex;
